import { ChevronDown } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { CountingNumber } from '@/components/ui/counting-number'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export interface KPIOption {
  key: string
  label: string
  value: number
  suffix?: string
  decimalPlaces?: number
  tooltip: string
  description?: string
}

interface KPICardProps {
  label: string
  value: number
  suffix?: string
  prefix?: string
  decimalPlaces?: number
  className?: string
  tooltip?: string
  description?: string
  /** Optional custom formatter — when provided, replaces the animated CountingNumber display */
  format?: (n: number) => string
  options?: KPIOption[]
  selectedOption?: string
  onOptionChange?: (key: string) => void
}

const cardClassNames = {
  container: 'flex-1 min-w-0 py-5 px-6 rounded-2xl shadow-sm',
  content: 'flex flex-col gap-2',
  label: 'text-sm text-muted-foreground',
  labelWrapper: 'flex items-center gap-1',
  labelDropdownTrigger:
    'flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors',
  valueWrapper: 'text-4xl font-semibold text-card-foreground',
  separator: 'border-t border-border mt-3 pt-3',
  description: 'text-xs text-muted-foreground',
}

function KPICardLabel({ label }: { label: string }) {
  return <p className={cardClassNames.label}>{label}</p>
}

function KPICardDropdownLabel({
  options,
  selectedOption,
  onOptionChange,
}: {
  options: KPIOption[]
  selectedOption: string
  onOptionChange: (key: string) => void
}) {
  const currentOption = options.find((opt) => opt.key === selectedOption)

  return (
    <div className={cardClassNames.labelWrapper}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className={cardClassNames.labelDropdownTrigger}>
            <span className={cardClassNames.label}>{currentOption?.label}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px]">
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.key}
              checked={selectedOption === option.key}
              onCheckedChange={() => onOptionChange(option.key)}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function KPICard({
  label,
  value,
  suffix,
  prefix,
  decimalPlaces = 0,
  className,
  description,
  format,
  options,
  selectedOption,
  onOptionChange,
}: KPICardProps) {
  // Dropdown variant
  if (options && selectedOption && onOptionChange) {
    const currentOption = options.find((opt) => opt.key === selectedOption)
    const displayValue = currentOption?.value ?? value
    const displaySuffix = currentOption?.suffix ?? suffix
    const displayDecimals = currentOption?.decimalPlaces ?? decimalPlaces
    const displayDescription = currentOption?.description ?? description

    return (
      <Card className={cn(cardClassNames.container, className)}>
        <div className={cardClassNames.content}>
          <KPICardDropdownLabel
            options={options}
            selectedOption={selectedOption}
            onOptionChange={onOptionChange}
          />
          <p className={cardClassNames.valueWrapper}>
            {prefix}
            <CountingNumber
              number={displayValue}
              decimalPlaces={displayDecimals}
              inView
              inViewOnce
            />
            {displaySuffix}
          </p>
          {displayDescription && (
            <div className={cardClassNames.separator}>
              <p className={cardClassNames.description}>{displayDescription}</p>
            </div>
          )}
        </div>
      </Card>
    )
  }

  // Simple variant
  return (
    <Card className={cn(cardClassNames.container, className)}>
      <div className={cardClassNames.content}>
        <KPICardLabel label={label} />
        <p className={cardClassNames.valueWrapper}>
          {prefix}
          {format ? (
            <span>{format(value)}</span>
          ) : (
            <CountingNumber
              number={value}
              decimalPlaces={decimalPlaces}
              inView
              inViewOnce
            />
          )}
          {suffix}
        </p>
        {description && (
          <div className={cardClassNames.separator}>
            <p className={cardClassNames.description}>{description}</p>
          </div>
        )}
      </div>
    </Card>
  )
}
